import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Accordion,
  FormGroup,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';

import useLocales from '../../../hooks/useLocales';
import { FormTypeTemplateProps } from '../../../@types/forms/types';

// ----------------------------------------------------------------------

type FormRelatedFormsProps = {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  formTemplatesList: FormTypeTemplateProps[];
  relatedFormTemplates: number[];
  selectedFormTemplate: FormTypeTemplateProps | null;
  currentFormId: number | undefined;
  handleFormTemplateInputChange: (value: FormTypeTemplateProps | null) => void;
  handleAddRelatedFormTemplate: () => void;
  handleDeleteRelatedFormTemplate: (index: number) => void;
};

const FormRelatedForms: React.FC<FormRelatedFormsProps> = ({
  isExpanded,
  setIsExpanded,
  formTemplatesList,
  relatedFormTemplates,
  selectedFormTemplate,
  currentFormId,
  handleFormTemplateInputChange,
  handleAddRelatedFormTemplate,
  handleDeleteRelatedFormTemplate,
}) => {
  const { t } = useLocales();

  return (
    <Accordion sx={{ p: 1 }} expanded={isExpanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={[
          {
            px: 3,
          },
          isExpanded
            ? {
                py: 3,
              }
            : {
                py: 2,
              },
        ]}
        onClick={() => setIsExpanded(!isExpanded)}
        id="8Pg0uq"
      >
        <Typography variant="h6"> {t('forms.relatedForms')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ py: 3, pl: 3, pr: 2, pt: 0 }}>
        <FormGroup
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Autocomplete
              id="edit-form-related-forms-select-template"
              value={selectedFormTemplate}
              onChange={(_, newInputValue) => {
                handleFormTemplateInputChange(newInputValue);
              }}
              options={formTemplatesList.filter((t) => {
                if (relatedFormTemplates.indexOf(t.id) === -1 && t.id !== currentFormId) {
                  return t.name;
                }
                return undefined;
              })}
              getOptionLabel={(option) => option.name}
              fullWidth
              size="small"
              renderInput={(params) => <TextField {...params} label={t('templates.templates')} />}
            />

            <IconButton
              disabled={selectedFormTemplate === null}
              onClick={() => handleAddRelatedFormTemplate()}
              id="qhzA5Z"
            >
              <AddIcon color={selectedFormTemplate ? 'primary' : 'disabled'} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              mt: 1,
              width: '100%',
              maxHeight: '160px',
              overflow: 'auto',
            }}
          >
            {relatedFormTemplates.length > 0 &&
              relatedFormTemplates.map((t, i) => (
                <Box
                  key={`form-template-${t}`}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography noWrap>
                    {formTemplatesList ? formTemplatesList.find((temp) => temp.id === t)?.name : t}
                  </Typography>

                  <IconButton onClick={() => handleDeleteRelatedFormTemplate(i)} id="ax8Xk3">
                    <ClearIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              ))}
          </Box>
        </FormGroup>
      </AccordionDetails>
    </Accordion>
  );
};

export default FormRelatedForms;
